import { ref, onMounted } from "vue";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { logger } from "@/services/logger/logger.service";
import {
  getSimplifiedDeviceString,
  parseUserAgent,
  getDeviceCategory,
} from "@/utils/deviceParser";

const FINGERPRINT_KEY = "scantix_device_id";
const FINGERPRINT_VERSION_KEY = "scantix_device_version";
const CURRENT_VERSION = "1.0";

// Global state for fingerprint
const visitorId = ref<string | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const metadata = ref<Record<string, unknown>>({});

// Initialize FingerprintJS agent (singleton)
let fpPromise: Promise<FingerprintJS.Agent> | null = null;

/**
 * Composable for device fingerprinting
 * Provides a unique device identifier for tracking and security
 */
export function useFingerprint() {
  /**
   * Initialize fingerprint if not already loaded
   */
  const initFingerprint = async () => {
    if (visitorId.value) {
      logger.debug("Fingerprint already loaded", {
        visitorId: visitorId.value,
      });
      return visitorId.value;
    }

    // Check localStorage for cached fingerprint
    const cached = localStorage.getItem(FINGERPRINT_KEY);
    const cachedVersion = localStorage.getItem(FINGERPRINT_VERSION_KEY);

    if (cached && cachedVersion === CURRENT_VERSION) {
      visitorId.value = cached;
      logger.debug("Using cached fingerprint", { visitorId: cached });
      return cached;
    }

    isLoading.value = true;
    error.value = null;

    try {
      // Initialize FingerprintJS agent only once
      if (!fpPromise) {
        fpPromise = FingerprintJS.load();
      }

      const fp = await fpPromise;
      const result = await fp.get();

      visitorId.value = result.visitorId;

      // Store additional metadata for debugging
      metadata.value = {
        confidence: result.confidence,
        components: Object.keys(result.components).length,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // Cache the fingerprint
      localStorage.setItem(FINGERPRINT_KEY, result.visitorId);
      localStorage.setItem(FINGERPRINT_VERSION_KEY, CURRENT_VERSION);

      logger.info("Device fingerprint generated", {
        visitorId: result.visitorId,
        confidence: result.confidence,
        metadata: metadata.value,
      });

      return result.visitorId;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to generate fingerprint";
      error.value = errorMessage;
      logger.error("Failed to generate fingerprint", { error: errorMessage });

      // Generate fallback fingerprint
      const fallback = generateFallbackFingerprint();
      visitorId.value = fallback;
      localStorage.setItem(FINGERPRINT_KEY, fallback);
      localStorage.setItem(FINGERPRINT_VERSION_KEY, CURRENT_VERSION);

      return fallback;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Generate a fallback fingerprint using basic browser properties
   */
  const generateFallbackFingerprint = (): string => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    let fingerprint = "";

    // Canvas fingerprinting
    if (ctx) {
      ctx.textBaseline = "top";
      ctx.font = '14px "Arial"';
      ctx.fillText("fingerprint", 2, 2);
      fingerprint += canvas.toDataURL();
    }

    // Combine various browser properties
    const browserData = [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth,
      screen.width,
      screen.height,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
      navigator.hardwareConcurrency || 0,
      navigator.platform,
    ].join("|");

    // Simple hash function
    let hash = 0;
    const combined = fingerprint + browserData;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    const fallbackId = `fallback_${Math.abs(hash).toString(36)}_${Date.now().toString(36)}`;

    logger.warn("Using fallback fingerprint", { fallbackId });
    return fallbackId;
  };

  /**
   * Clear cached fingerprint and regenerate
   */
  const resetFingerprint = async () => {
    localStorage.removeItem(FINGERPRINT_KEY);
    localStorage.removeItem(FINGERPRINT_VERSION_KEY);
    visitorId.value = null;
    metadata.value = {};
    fpPromise = null;

    logger.info("Fingerprint reset");
    return await initFingerprint();
  };

  /**
   * Get device info for API requests
   */
  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    const deviceInfo = parseUserAgent(userAgent);

    return {
      deviceId: visitorId.value,
      fingerprint: visitorId.value, // Alias for backwards compatibility
      userAgent: navigator.userAgent,
      // New simplified fields
      deviceType: deviceInfo.type,
      deviceOS: deviceInfo.os,
      deviceBrowser: deviceInfo.browser,
      deviceCategory: getDeviceCategory(userAgent),
      deviceString: getSimplifiedDeviceString(userAgent),
      // Original fields
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
      ...metadata.value,
    };
  };

  // Auto-initialize on mount
  onMounted(() => {
    initFingerprint();
  });

  return {
    visitorId,
    deviceId: visitorId, // Alias
    isLoading,
    error,
    metadata,
    initFingerprint,
    resetFingerprint,
    getDeviceInfo,
  };
}

// Export a singleton instance for global use
let globalFingerprint: ReturnType<typeof useFingerprint> | null = null;

export function getGlobalFingerprint() {
  if (!globalFingerprint) {
    globalFingerprint = useFingerprint();
  }
  return globalFingerprint;
}
